// Tiny pub/sub bus. Modules emit and subscribe; no direct cross-module calls.
// Keep this file small — it's the contract between subsystems.

const listeners = new Map()

export function on(event, fn) {
  if (!listeners.has(event)) listeners.set(event, new Set())
  listeners.get(event).add(fn)
  return () => off(event, fn)
}

export function off(event, fn) {
  listeners.get(event)?.delete(fn)
}

export function emit(event, payload) {
  const set = listeners.get(event)
  if (!set) return
  for (const fn of set) {
    try {
      fn(payload)
    } catch (err) {
      console.error(`[eventBus] listener for "${event}" threw:`, err)
    }
  }
}

// Canonical event names. Use these constants — no string literals at call sites.
export const Events = Object.freeze({
  Click: 'click',
  SeasonChange: 'seasonChange',
  SeasonProgress: 'seasonProgress', // { season, elapsed, duration, clicks } — time-boxed season bar
  Harvest: 'harvest',               // { harvestable, bonus } — end-of-HARVEST payout (cap × riceValue)
  TaxRateChange: 'taxRateChange',   // { rate } — player adjusted the citizen tax rate (CR-4)
  HonorChange: 'honorChange',       // { honor } — Honor rating recomputed (CR-4)
  WealthChange: 'wealthChange',
  ClickCountChange: 'clickCountChange',
  GoonOwnedChange: 'goonOwnedChange',
  GoonBuyRequested: 'goonBuyRequested',
  GoonClick: 'goonClick',
  RowFilled: 'rowFilled',           // a crop row just filled with the player's clicks ({ k, rows, full }) — scene/riceField.js
  Surplus: 'surplus',               // a click past a full field ({ surplus, target }) — it still counts toward the harvest
  ComboChange: 'comboChange',
  GlintUp: 'glintUp',               // a golden glint is up on the field (scene/glintFx picks the row) — game/glint.js
  GlintGone: 'glintGone',           // …its window passed
  GlintCaught: 'glintCaught',       // …a paw click caught it ({ boostClicks, boostMult })
  CitizenTended: 'citizenTended',   // a directed citizen's kneel paid ({ goonId, delta, clicks }) — seasonManager.registerTend
  CoinPopAt: 'coinPopAt',           // a coin to float on the screen ({ x, y, delta } in CSS px) — ui/hud.js
  CrowSweep: 'crowSweep',           // a crow's shadow is sweeping in ({ k })
  CrowLand: 'crowLand',             // the crow landed on row k ({ k, key, ri }) — click it!
  CrowShooed: 'crowShooed',         // …and was clicked off ({ k })
  CrowAte: 'crowAte',               // …or ate a notch ({ k, lost })
  ClicksLost: 'clicksLost',
  OpenHospital: 'openHospital',     // open the Hospital pop-up (the badge over the building, the rail card) — ui/hospitalChip.js
  MessageShown: 'messageShown',     // a card rose onto the message rail ({ key, kind }) — ui/messageRail.js
  CitizenKneels: 'citizenKneels',   // a citizen kneels to tend ({ goonId }) — scene/peasants.js         // this season's clicks went down ({ lost, clicks, why }) — seasonManager.loseClicks       // the rhythm combo moved ({ value, mult, live, max, broke }) — game/seasonManager.js / loopFeedback.js
  BuildingAdded: 'buildingAdded',
  PlacementStart: 'placementStart',
  PlacementCommit: 'placementCommit',
  PlacementCancel: 'placementCancel',
  SessionBlocked: 'sessionBlocked',     // { reason: 'tab' | 'device' } — another window/device is the active one; this one stops saving
  PlacementPending: 'placementPending', // { type, tileIndex } — a building was dropped and awaits Confirm / Reposition / Cancel
  PlacementResolved: 'placementResolved', // { result: 'confirm'|'cancel'|'reposition' }
  BuildingRemoved: 'buildingRemoved',   // { type, tileIndex }
  BuildingRotated: 'buildingRotated',   // { tileIndex, rotation }
  FeedBuyRequested: 'feedBuyRequested',
  FeedActivated: 'feedActivated',
  TaxOfflineEarned: 'taxOfflineEarned',
  KingdomChange: 'kingdomChange',
  KingdomUnlocked: 'kingdomUnlocked',
  KingdomSwitchRequested: 'kingdomSwitchRequested',
  KingdomUnlockRequested: 'kingdomUnlockRequested',
  MarriageRequested: 'marriageRequested',
  EducationRequested: 'educationRequested',
  EducationLevelChange: 'educationLevelChange',
  MigrationRequested: 'migrationRequested',
  EventTriggered: 'eventTriggered',
  EventCleared: 'eventCleared',
  NarrativeBeat: 'narrativeBeat',
  ProfileChange: 'profileChange',
  OnboardingComplete: 'onboardingComplete',
  KingdomViewRequested: 'kingdomViewRequested',
  LicensePurchased: 'licensePurchased',
  DegreePurchased: 'degreePurchased',
  OpenStudy: 'openStudy',         // { subject } — open the study-guide reading page
  OpenQuiz: 'openQuiz',           // { subject, tier } — request to sit a degree quiz
  QuizFinished: 'quizFinished',   // { subject, tier, score, passed, earned }
  DefectRequested: 'defectRequested',
  ElectionApplied: 'electionApplied',
  ElectionWon: 'electionWon',
  ElectionLost: 'electionLost',
  ElectionResigned: 'electionResigned',
  AccountReady: 'accountReady',
  AccountStatePushed: 'accountStatePushed',
  AccountStatePulled: 'accountStatePulled',
  PageChange: 'pageChange',
  IdleWarn: 'idleWarn',
  IdleFineInitial: 'idleFineInitial',
  IdleFineRecurring: 'idleFineRecurring',
  IdleResume: 'idleResume',
  PauseChange: 'pauseChange',   // { paused } — player paused/resumed the game
  WildGoonConverted: 'wildGoonConverted',
  WildGoonBrawled: 'wildGoonBrawled',   // { waveId } — citizens brought a goon down (they were injured doing it)
  CitizenEducated: 'citizenEducated', // { count } — a citizen graduated from a School
  CitizenPromoted: 'citizenPromoted', // { id, role } — a citizen promoted to a trade (CR-8)
  RestChange: 'restChange',           // { resting, until, auto } — citizens sent to rest / back to work
  WorkModeChange: 'workModeChange',   // { mode } — Fields ⇄ Village worker assignment
  CitizenInjured: 'citizenInjured',   // { count } — a citizen got hurt fighting a goon
  CitizenHealed: 'citizenHealed',     // healed at a Hospital / fed back to health
  CitizenRestless: 'citizenRestless', // { goonId } — a citizen is growing restless: school or bless it within FeralConfig.restlessMs or it turns feral
  CitizenFeral: 'citizenFeral',       // a neglected citizen turned feral (→ wild goon)
  WildGoonKilled: 'wildGoonKilled',
  WildGoonSlain: 'wildGoonSlain',
  PlotChanged: 'plotChanged',
  HomeLevelChange: 'homeLevelChange', // { tileIndex, level } — home shack→manor upgrade (CR-5)
  PlotPurchaseRequested: 'plotPurchaseRequested',
  HackHome: 'hackHome',
  RotateView: 'rotateView', // { dir: -1 | 1 } — snap the iso camera a quarter-turn (Q/E + on-screen arrows)
  CatHungerChange: 'catHungerChange',
  CatStarvationDeath: 'catStarvationDeath',
  CitizenAdmitted: 'citizenAdmitted',     // { goonId } — a starving citizen is on its way to the Hospital (scene: walk it in)
  PatientsHealed: 'patientsHealed',       // { count, cost } — every patient was healed and released
  PatientDied: 'patientDied',             // { goonId } — a patient was left too long
  HospitalChange: 'hospitalChange',       // { count } — the number of patients changed
  GravesChange: 'gravesChange',           // { graves } — a citizen was lost (a headstone is added)
  FriendlyFire: 'friendlyFire',
  MembershipChange: 'membershipChange',
  MarriageProposalSent: 'marriageProposalSent',
  MarriageAccepted: 'marriageAccepted',
  MarriageDivorced: 'marriageDivorced',
  OpenToMarriageChange: 'openToMarriageChange',
  RegistryOpen: 'registryOpen', // open the registry panel (self or other player)
  OpenSettings: 'openSettings', // open the settings modal (from the registry header gear)
  OpenCloud: 'openCloud',       // open the cloud-sync panel (from the registry header)
  OpenCloudLogin: 'openCloudLogin', // (legacy) open the CLOUD panel on the LOG IN tab
  OpenLogin: 'openLogin',           // open the site-level Log in modal (header "Log in" button)
  OpenControllerSetup: 'openControllerSetup', // open the gamepad remap wizard
  StartAdoption: 'startAdoption',   // open the adopt-a-Mao / Register flow (header "Register" + home CTA)
  ChildBorn: 'childBorn',
  InboxUnreadChange: 'inboxUnreadChange', // { unread } — marriage inbox indicator
  CoopChange: 'coopChange',               // { host, guest, inEstate } — stays this player hosts / is a guest in changed (src/game/coopManager.js)
  HouseholdDue: 'householdDue',           // { wives } — a husband has signed in: show his household report (once per sign-in; src/game/inviteManager.js → ui/householdReport.js)
  NeedGender: 'needGender',               // the signed-in, adopted Mao has no gender yet: ask once (src/game/genderManager.js → ui/genderPrompt.js)
  CoopNotice: 'coopNotice',               // { text, kind: 'ok'|'warn', feed? } — something truthful the guest / host should be told about a visit
  CoopVisitStart: 'coopVisitStart',       // { stay } — the player just accepted an invitation: travel to the host's kingdom
  InvitesChange: 'invitesChange',         // { received, sent, blocked, attention } — co-op invitations changed (src/game/inviteManager.js)
  AchievementUnlocked: 'achievementUnlocked', // { id, name, desc, rarity, awardedAt }
  ToolModeChange: 'toolModeChange', // { mode } — OFF | DEFENSE | POSITIVE (1/2 keys)
  StreakChange: 'streakChange', // { current, longest } — login streak in days
  StreakRewardClaimed: 'streakRewardClaimed', // { dayNum, amount } — milestone bonus paid out
  HolidayChange: 'holidayChange', // { holiday: HolidayEntry | null } — entered/left a festival window
  HolidayParticipationStamp: 'holidayParticipationStamp', // { token, at, holiday } — new token added to holidaysSeen

  // Adventures — cat leaves the kingdom on a quest. AdventureStart fires when
  // an adventure begins, AdventureEnd when it finishes (or is recalled).
  AdventureStart:        'adventureStart',        // { adventure, mode }
  AdventureEnd:          'adventureEnd',          // { adventure, reason: 'complete'|'recall', payout, discoveries }
  AdventureModeChange:   'adventureModeChange',   // { mode } — DISCOVERY|DEFENSE switch
  AdventureDiscovery:    'adventureDiscovery',    // { item, amount, biome }
  AdventureScribeBeat:   'adventureScribeBeat',   // { kind, message } — pigeon/smoke/courier
  AdventureTick:         'adventureTick',         // { elapsed, remaining, progress } — for panel countdown
  AdventureCompleted:    'AdventureCompleted',    // { adventure } — fired on auto-completion only (not recall)

  // The Capital — a separate walkable city the hero can visit (capitalScene.js).
  VisitCapital:  'visitCapital',   // ask to travel to the capital
  VisitLand:     'visitLand',      // { id } — ask to travel to one of the other adventure lands (Bamboo Badlands …)
  LeaveLand:     'leaveLand',      // ask to return home from a land
  LandChange:    'landChange',     // { id, active, busy } — a land: travelling / arrived / returned
  LeaveCapital:  'leaveCapital',   // ask to return home
  AwayChange:    'awayChange',     // { id, active, busy } — the cat is away in ANY place (drives RETURN HOME and the locked rail buttons)
  CapitalChange: 'capitalChange',  // { active, busy } — travelling/arrived/returned (drives the button)
  TripLedgerChange: 'tripLedgerChange', // { entry? } — the Capital trip ledger changed (panel refresh)
  TripAlert:     'tripAlert',      // { entry } — a ledger entry important enough to interrupt (severity: whisper|smoke|drums|banner)
  CapitalLeadsOpened: 'capitalLeadsOpened', // { names, gated, from } — new exploration leads (faint rings) just appeared in the Capital
  CapitalPoiFound: 'capitalPoiFound', // { poi } — the hero discovered a landmark in the Capital (first time ever)
  CapitalReturn: 'capitalReturn',  // { summary } — hero is home; show the "welcome back" tally
  RaidWave:      'raidWave',       // { count, alive } — a wave of raiders really spawned (so alerts can quote real numbers)

  AdminOpen: 'adminOpen',
  RaidStart: 'raidStart',
  RaidEnd: 'raidEnd',
  BuildingDamaged: 'buildingDamaged',
  PlotDamaged: 'plotDamaged',
  RepairStart: 'repairStart',     // { mode: 'paid'|'labor' }
  RepairComplete: 'repairComplete',
})

// DEV: the app's own bus instance, so tests can emit real events (a duplicate module import would not reach the HUD).
if (import.meta.env?.DEV) window.__bus = { emit, on, Events } // (on: so a test can listen)
